import { infoBarAddHandler, infoBarRemoveHandler } from './infoBarHandlers';
import setNumberOfWaitingAttachments from '../actions/setNumberOfWaitingAttachments';
import trySaveMessage, { waitForActiveSaving } from '../actions/trySaveMessage';
import { ComposeViewState } from 'owa-mail-compose-store';
import { logUsage } from 'owa-analytics';
import { AttachmentFile, AttachmentFileList } from 'owa-attachment-file-types';
import { ClientAttachmentId, getUserMailboxInfo } from 'owa-client-ids';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import triggerPolicyTips from 'owa-mail-compose-actions/lib/actions/triggerPolicyTips';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import { lazyCreateSmimeAttachmentsFromFiles } from 'owa-smime';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { trace } from 'owa-trace';
import {
    lazyCreateAttachmentsViaQueueManager,
    CreateAttachmentsProperties,
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from 'owa-attachment-well-data';

function createAttachmentsHandler(
    files: AttachmentFileList,
    composeViewState: ComposeViewState,
    properties: CreateAttachmentsProperties,
    processInlineImageBeforeUploadFunc?: (temporaryId: string, file: AttachmentFile) => void,
    attachmentInfoBarAddHandler?: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    attachmentInfoBarRemoveHandler?: (infoBarMessageIds: string[]) => void,
    onAllAttachmentsProcessed?: (attachmentId: ClientAttachmentId) => void,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback,
    tryAgainCallback?: () => void
) {
    if (isSmimeEnabledInViewState(composeViewState.smimeViewState)) {
        lazyCreateSmimeAttachmentsFromFiles.import().then(async createSmimeAttachmentsFromFiles => {
            try {
                const filesQueuedToAttach = await createSmimeAttachmentsFromFiles(
                    files,
                    {
                        ...composeViewState.itemId,
                        mailboxInfo: getUserMailboxInfo(),
                    },
                    composeViewState.attachmentWell,
                    properties,
                    attachmentInfoBarAddHandler
                        ? attachmentInfoBarAddHandler
                        : infoBarAddHandler(composeViewState),
                    attachmentInfoBarRemoveHandler
                        ? attachmentInfoBarRemoveHandler
                        : infoBarRemoveHandler(composeViewState)
                );

                inlineAttachmentsHandler(
                    properties,
                    filesQueuedToAttach,
                    composeViewState,
                    processInlineImageBeforeUploadFunc
                );
            } catch (e) {
                logUsage(
                    'CreateSmimeAttachmentsFromFilesErrorDatapoint',
                    { errorMessage: e.message },
                    { isCore: true }
                );
                trace.warn(e);
            }
        });
    } else {
        lazyCreateAttachmentsViaQueueManager.import().then(createAttachmentsViaQueueManager => {
            const filesQueuedToAttach = createAttachmentsViaQueueManager(
                composeViewState.attachmentWell,
                {
                    ...composeViewState.itemId,
                    mailboxInfo: properties.mailboxInfo
                        ? properties.mailboxInfo
                        : getUserMailboxInfo(),
                },
                files,
                attachmentInfoBarAddHandler
                    ? attachmentInfoBarAddHandler
                    : infoBarAddHandler(composeViewState),
                attachmentInfoBarRemoveHandler
                    ? attachmentInfoBarRemoveHandler
                    : infoBarRemoveHandler(composeViewState),
                properties,
                onAllAttachmentsProcessed,
                onAttachmentCreatedCallback,
                onAttachmentCanceledOrFailedCallback,
                tryAgainCallback
            );

            inlineAttachmentsHandler(
                properties,
                filesQueuedToAttach,
                composeViewState,
                processInlineImageBeforeUploadFunc
            );
        });
    }
}

function inlineAttachmentsHandler(
    properties: CreateAttachmentsProperties,
    filesQueuedToAttach: AttachmentFileList,
    composeViewState: ComposeViewState,
    processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void
) {
    if (properties.isInline) {
        const fileLength = filesQueuedToAttach.length;
        const inlineAttachmentsLength = composeViewState.attachmentWell.inlineAttachments.length;
        for (let i = 0; i < fileLength; i++) {
            // Get the last fileLength number of inline attachments
            // since they are the new ones created from above createAttachmentsViaQueueManager.
            const attachmentState =
                composeViewState.attachmentWell.inlineAttachments[
                    inlineAttachmentsLength - fileLength + i
                ];
            processInlineImageBeforeUploadFunc &&
                processInlineImageBeforeUploadFunc(
                    attachmentState.placeholderId,
                    filesQueuedToAttach[i]
                );
        }
    }
}

export default async function createAttachments(
    files: AttachmentFileList,
    composeViewState: ComposeViewState,
    properties: CreateAttachmentsProperties,
    processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void,
    attachmentInfoBarAddHandler?: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    attachmentInfoBarRemoveHandler?: (infoBarMessageIds: string[]) => void,
    onAllAttachmentsProcessed?: (attachmentId: ClientAttachmentId) => void,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback,
    tryAgainCallback?: () => void
) {
    let attachmentIsWaiting = false;

    // We are checking for S/MIME mail here, because we don't need an itemId to create S/MIME attachments
    // ItemId is not required because S/MIME attachments are not uploaded to the server.
    const isSmimeCompose = isSmimeEnabledInViewState(composeViewState.smimeViewState);

    if (!composeViewState.itemId && !isSmimeCompose) {
        // Until item id is retrieved, attachment creation is put on hold
        // and messages are prevented from being sent with a wait dialog.
        // This mimics the behavior in jsMVVM (VSO 33271)
        attachmentIsWaiting = true;
        setNumberOfWaitingAttachments(
            composeViewState,
            composeViewState.numberOfWaitingAttachments + 1
        );
    }

    try {
        if (
            !composeViewState.itemId &&
            !isSmimeCompose &&
            !(await waitForActiveSaving(composeViewState))
        ) {
            await trySaveMessage(composeViewState);
        }

        createAttachmentsHandler(
            files,
            composeViewState,
            properties,
            processInlineImageBeforeUploadFunc,
            attachmentInfoBarAddHandler,
            attachmentInfoBarRemoveHandler,
            attachmentId => {
                if (onAllAttachmentsProcessed) {
                    onAllAttachmentsProcessed(attachmentId);
                }
                if (isPolicyTipsEnabled()) {
                    triggerPolicyTips(composeViewState, EventTrigger.AttachmentAdded);
                }
            },
            onAttachmentCreatedCallback,
            onAttachmentCanceledOrFailedCallback,
            tryAgainCallback
        );
    } finally {
        if (attachmentIsWaiting) {
            // Always decrement the number of waiting attachments in the end,
            // even if the save failed and the attachment was not handled, so
            // that the user can still send the message
            setNumberOfWaitingAttachments(
                composeViewState,
                composeViewState.numberOfWaitingAttachments - 1
            );
        }
    }
}
